import { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useCMSCashierBonuses } from '@/modules/bonuses';
import { useDepositStore, useGetCommonStore } from '@/store';
import { useProgressiveOfferBonuses } from './useProgressiveOfferBonuses';

export enum Tabs {
  BONUS = 0,
  PROMOCODE = 1,
}

export const useIsBonusCardCode = () => {
  const useCommonStore = useGetCommonStore();
  const { user, pendingUser } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      pendingUser: state.pendingUser,
    }))
  );

  const initialBonusTab = useDepositStore.use.initialBonusTab();
  const setInitialBonusTab = useDepositStore.use.setInitialBonusTab();

  const depositBonusCode = user?.depositBonusCode;

  const { bonuses: cashierBonuses, isLoading: isCashierBonusesLoading } =
    useCMSCashierBonuses();
  const {
    progressiveBonusGroups,
    hasCMSProgressiveData,
    isLoading: isProgressiveBonusesLoading,
  } = useProgressiveOfferBonuses();

  const isDepositBonusCodeFromBonuses = useMemo(
    () =>
      !!cashierBonuses.find(
        ({ code }) => !!depositBonusCode && depositBonusCode === code
      ),
    [cashierBonuses, depositBonusCode]
  );

  const isDepositBonusCodeFromProgressiveBonuses = useMemo(
    () =>
      hasCMSProgressiveData &&
      !!depositBonusCode &&
      progressiveBonusGroups.some((g) =>
        g.bonuses.some((b) => b.code === depositBonusCode)
      ),
    [progressiveBonusGroups, hasCMSProgressiveData, depositBonusCode]
  );

  const isBonusCardCode =
    isDepositBonusCodeFromBonuses || isDepositBonusCodeFromProgressiveBonuses;

  const [activeTab, setActiveTab] = useState(-1);
  const hasUserSwitchedTabRef = useRef(false);

  useEffect(() => {
    if (pendingUser || activeTab !== -1) return;
    if (
      depositBonusCode &&
      (isCashierBonusesLoading || isProgressiveBonusesLoading)
    )
      return;

    if (initialBonusTab !== null) {
      setActiveTab(initialBonusTab);
      setInitialBonusTab(null);
    } else if (depositBonusCode && !isBonusCardCode) {
      setActiveTab(Tabs.PROMOCODE);
    } else {
      setActiveTab(Tabs.BONUS);
    }
  }, [
    depositBonusCode,
    pendingUser,
    activeTab,
    isBonusCardCode,
    isCashierBonusesLoading,
    isProgressiveBonusesLoading,
    initialBonusTab,
    setInitialBonusTab,
  ]);

  useEffect(() => {
    if (!depositBonusCode || pendingUser) return;
    if (isCashierBonusesLoading || isProgressiveBonusesLoading) return;

    const canIssueBonuses = Boolean(user?.canIssueBonuses);
    if (!isBonusCardCode && canIssueBonuses && activeTab !== Tabs.BONUS) {
      setActiveTab(Tabs.PROMOCODE);
    }
  }, [
    depositBonusCode,
    isBonusCardCode,
    user?.canIssueBonuses,
    pendingUser,
    activeTab,
    isCashierBonusesLoading,
    isProgressiveBonusesLoading,
  ]);

  useEffect(() => {
    if (!depositBonusCode || pendingUser || !isBonusCardCode) return;
    if (hasUserSwitchedTabRef.current) return;
    setActiveTab(Tabs.BONUS);
  }, [depositBonusCode, isBonusCardCode, pendingUser]);

  return {
    activeTab,
    setActiveTab,
    hasUserSwitchedTabRef,
    isBonusCardCode,
    cashierBonuses,
    isCashierBonusesLoading,
    progressiveBonusGroups,
    hasCMSProgressiveData,
    isProgressiveBonusesLoading,
    isLoading: isCashierBonusesLoading || isProgressiveBonusesLoading,
  };
};
